import { useMemo, useState } from "react";
import {
    useFeatureFlagOverrides,
    useOrganizationFeatureFlagOverrides,
    useResetFeatureFlagOverridesMutation,
    useResetOrganizationFeatureFlagOverridesMutation,
    useUpdateFeatureFlagOverrideMutation,
    useUpdateOrganizationFeatureFlagOverrideMutation,
} from "@api/featureFlagOverride.api";
import { SearchIcon } from "@icons";
import { Input } from "@design-system/Input/Input";
import { SettingsPanel } from "@design-system/SettingsPanel/SettingsPanel.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { type DemoFeatureFlagScope } from "@hooks/useDemoFeatureFlagScope";
import { DemoFlagRow } from "./DemoFlagRow";
import { useBaseFlagValues } from "./useBaseFlagValues";
import {
    useDemoFeatureFlagsTarget,
    useFeatureFlagsPanelOpen,
} from "./featureFlagsPanel";
import { groupDemoFlags } from "./groupDemoFlags";
import styles from "./DemoFeatureFlagsPanel.module.scss";

const EMPTY_OVERRIDES: Record<string, boolean> = {};
const EMPTY_FLAGS: string[] = [];
const ORGANIZATION_NOTE =
    "Applies to all demo clients unless a client overrides it.";

interface PanelOpenProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
}

interface DemoFlagsPanelBodyProps extends PanelOpenProps {
    note?: string;
    overrides: Record<string, boolean> | undefined;
    organizationOverrides?: Record<string, boolean>;
    uiOnlyFlags: string[] | undefined;
    updateOverride: (
        variables: { flag: string; isEnabled: boolean | null },
        options: { onError: () => void },
    ) => void;
    resetOverrides: (
        variables: undefined,
        options: { onError: () => void },
    ) => void;
    isResetting: boolean;
}

const DemoFlagsPanelBody = ({
    isOpen,
    onOpenChange,
    note,
    overrides = EMPTY_OVERRIDES,
    organizationOverrides = EMPTY_OVERRIDES,
    uiOnlyFlags = EMPTY_FLAGS,
    updateOverride,
    resetOverrides,
    isResetting,
}: DemoFlagsPanelBodyProps) => {
    const [search, setSearch] = useState("");
    const { error: toastError } = useToaster();
    const baseFlagValues = useBaseFlagValues();
    const { overriddenFlags, firmFlags, otherFlags } = useMemo(
        () =>
            groupDemoFlags({
                baseFlagValues,
                overrides,
                organizationOverrides,
                search,
            }),
        [baseFlagValues, overrides, organizationOverrides, search],
    );
    const uiOnlyFlagSet = useMemo(() => new Set(uiOnlyFlags), [uiOnlyFlags]);
    const hasOverrides = Object.keys(overrides).length > 0;

    const update = (flag: string, isEnabled: boolean | null) => {
        updateOverride(
            { flag, isEnabled },
            { onError: () => toastError("Failed to update feature flag") },
        );
    };

    return (
        <SettingsPanel.Root
            open={isOpen}
            onOpenChange={onOpenChange}
            trigger={<span aria-hidden className={styles.hiddenTrigger} />}
            footer={
                hasOverrides && (
                    <SettingsPanel.Action
                        disabled={isResetting}
                        onClick={() =>
                            resetOverrides(undefined, {
                                onError: () =>
                                    toastError("Failed to reset feature flags"),
                            })
                        }
                    >
                        Reset all
                    </SettingsPanel.Action>
                )
            }
        >
            {note && <p className={styles.note}>{note}</p>}
            <div className={styles.search}>
                <Input
                    aria-label="Search feature flags"
                    placeholder="Search feature flags"
                    leftIcon={<SearchIcon />}
                    clearable
                    value={search}
                    onValueChange={setSearch}
                    onClear={() => setSearch("")}
                />
            </div>
            {overriddenFlags.length > 0 && (
                <SettingsPanel.Section label="Overridden">
                    {overriddenFlags.map(({ flag, isEnabled }) => (
                        <DemoFlagRow
                            key={flag}
                            flag={flag}
                            checked={isEnabled}
                            isUiOnly={uiOnlyFlagSet.has(flag)}
                            onCheckedChange={(checked) => update(flag, checked)}
                            onReset={() => update(flag, null)}
                        />
                    ))}
                </SettingsPanel.Section>
            )}
            {firmFlags.length > 0 && (
                <SettingsPanel.Section label="From firm">
                    {firmFlags.map(({ flag, isEnabled }) => (
                        <DemoFlagRow
                            key={flag}
                            flag={flag}
                            checked={isEnabled}
                            isUiOnly={uiOnlyFlagSet.has(flag)}
                            isFromFirm
                            onCheckedChange={(checked) => update(flag, checked)}
                        />
                    ))}
                </SettingsPanel.Section>
            )}
            <SettingsPanel.Section label="All flags">
                {otherFlags.length === 0 ? (
                    <SettingsPanel.Empty>No feature flags</SettingsPanel.Empty>
                ) : (
                    otherFlags.map(({ flag, isEnabled }) => (
                        <DemoFlagRow
                            key={flag}
                            flag={flag}
                            checked={isEnabled}
                            isUiOnly={uiOnlyFlagSet.has(flag)}
                            onCheckedChange={(checked) => update(flag, checked)}
                        />
                    ))
                )}
            </SettingsPanel.Section>
        </SettingsPanel.Root>
    );
};

const WorkspaceFlagsPanel = ({
    workspaceId,
    ...openProps
}: PanelOpenProps & { workspaceId: string }) => {
    const { data } = useFeatureFlagOverrides(workspaceId, { enabled: true });
    const updateOverride = useUpdateFeatureFlagOverrideMutation(workspaceId);
    const resetOverrides = useResetFeatureFlagOverridesMutation(workspaceId);

    return (
        <DemoFlagsPanelBody
            {...openProps}
            overrides={data?.overrides}
            organizationOverrides={data?.organizationOverrides}
            uiOnlyFlags={data?.uiOnlyFlags}
            updateOverride={updateOverride.mutate}
            resetOverrides={resetOverrides.mutate}
            isResetting={resetOverrides.isPending}
        />
    );
};

const OrganizationFlagsPanel = ({
    organizationId,
    ...openProps
}: PanelOpenProps & { organizationId: string }) => {
    const { data } = useOrganizationFeatureFlagOverrides(organizationId, {
        enabled: true,
    });
    const updateOverride =
        useUpdateOrganizationFeatureFlagOverrideMutation(organizationId);
    const resetOverrides =
        useResetOrganizationFeatureFlagOverridesMutation(organizationId);

    return (
        <DemoFlagsPanelBody
            {...openProps}
            note={ORGANIZATION_NOTE}
            overrides={data?.overrides}
            uiOnlyFlags={data?.uiOnlyFlags}
            updateOverride={updateOverride.mutate}
            resetOverrides={resetOverrides.mutate}
            isResetting={resetOverrides.isPending}
        />
    );
};

const DemoFeatureFlagsPanelContent = ({
    target,
    ...openProps
}: PanelOpenProps & { target: DemoFeatureFlagScope }) =>
    target.kind === "workspace" ? (
        <WorkspaceFlagsPanel
            key={target.workspaceId}
            workspaceId={target.workspaceId}
            {...openProps}
        />
    ) : (
        <OrganizationFlagsPanel
            key={target.organizationId}
            organizationId={target.organizationId}
            {...openProps}
        />
    );

export const DemoFeatureFlagsPanel = () => {
    const target = useDemoFeatureFlagsTarget();
    const { isOpen, onOpenChange } = useFeatureFlagsPanelOpen();

    if (!target) {
        return null;
    }

    return (
        <div className={styles.anchor}>
            <DemoFeatureFlagsPanelContent
                target={target}
                isOpen={isOpen}
                onOpenChange={onOpenChange}
            />
        </div>
    );
};
